import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { MfaPreferences } from 'src/enums'
import { translateNode } from 'src/localization/translateNode'
import { getRequestConfig } from 'src/queries/requestConfig'
import {
  DELETE_MFA_PREFERENCE_FAILURE,
  DELETE_MFA_PREFERENCE_INIT,
  DELETE_MFA_PREFERENCE_SUCCESS,
  getMe,
  POST_MFA_PREFERENCE_FAILURE,
  POST_MFA_PREFERENCE_INIT,
  POST_MFA_PREFERENCE_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { BackendErrorPayload } from 'src/utils/error.utils'

type BackendErrorResponse = { response: { data: BackendErrorPayload<string> } }

type MfaPreferenceResponse = { type: string }

type UsePostMfaPreferenceVariables = {
  userName: string
  preference?: MfaPreferences
}

/** Turn on two-factor authentication. Preserves legacy thunk actions/toasts. */
export const usePostMfaPreference = () => {
  const dispatch = useDispatch()

  return useMutation({
    mutationFn: ({
      userName,
      preference,
    }: UsePostMfaPreferenceVariables): Promise<void> => {
      dispatch({ type: POST_MFA_PREFERENCE_INIT })
      return axios
        .post<MfaPreferenceResponse>(
          `/mfa`,
          {
            cognitoId: userName,
            preferred: preference || MfaPreferences.email,
          },
          getRequestConfig(),
        )
        .then(
          async (response) => {
            dispatch({
              type: POST_MFA_PREFERENCE_SUCCESS,
              payload: response.data,
            })
            dispatch(getMe()) // reload current user
            dispatch(createSetIsToastOpen(false))
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: translateNode(
                  "You've turned on two-factor authentication",
                ),
              }),
            )
          },
          (err: BackendErrorResponse) => {
            dispatch({
              type: POST_MFA_PREFERENCE_FAILURE,
              payload: err.response.data,
            })
          },
        )
    },
  })
}

/** Turn off two-factor authentication. Preserves legacy thunk actions/toasts. */
export const useDeleteMfaPreference = () => {
  const dispatch = useDispatch()

  return useMutation({
    mutationFn: (): Promise<void> => {
      dispatch({ type: DELETE_MFA_PREFERENCE_INIT })
      return axios
        .delete<MfaPreferenceResponse>(`/mfa`, getRequestConfig())
        .then(
          async (response) => {
            dispatch({
              type: DELETE_MFA_PREFERENCE_SUCCESS,
              payload: response.data,
            })
            dispatch(getMe()) // reload current user
            dispatch(createSetIsToastOpen(false))
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: translateNode(
                  "You've turned off two-factor authentication",
                ),
              }),
            )
          },
          (err: BackendErrorResponse) => {
            dispatch({
              type: DELETE_MFA_PREFERENCE_FAILURE,
              payload: err.response.data,
            })
          },
        )
    },
  })
}
